import {ComponentType, FC, ReactNode, useMemo} from 'react';

import {createView, view, type View} from '@sail/ui';

import {
  Conversation,
  Observability,
} from '@stripe-internal/conversations-client';
import {InlineDataComponentRegistry} from '@stripe-internal/inline-data';
import type {MessagePartComponentRegistry} from '@stripe-internal/conversations-ui';
import {type Theme} from '@stripe-internal/conversations-ui';
import {Visibility} from '@stripe-internal/support-conversations-controller';

import {AppCloseHandler, AppConfig, AppProfile} from '../core/types';
import {ComponentWithOverride} from '../core/components/ComponentWithOverride';
import {DefaultErrorBoundary} from '../core/components/ErrorBoundary';
import {ParentMessageBus} from '../core/lib/parentMessageBus/ParentMessageBus';
import {ThreadsAssistantProviders} from './providers/ThreadsAssistantProviders';
import {ThreadsContainer} from './components/ThreadsContainer';
import {ThreadsConversationScreen} from './components/ConversationScreen/ThreadsConversationScreen';
import {ThreadsHistoryDrawer} from './components/HistoryScreen/ThreadsHistoryDrawer';
import {ThreadsLiveChatIframe} from './components/ThreadsLiveChatIframe';
import {useThreadsVisibility, useThreadsWithHistory} from './app/hooks';

type Props = View.IntrinsicElement<
  'div',
  {
    config: AppConfig;
    profile: AppProfile;
    withHistory?: boolean;
    withLiveChat?: boolean;
    clientFetch?: typeof fetch;
    parentMessageBus?: ParentMessageBus;
    historyDrawer?: ReactNode | ComponentType;
    header?: ReactNode | ComponentType<{conversation?: Conversation | null}>;
    content?:
      | ReactNode
      | ComponentType<{conversation?: Conversation | null; loading?: boolean}>;
    log?: ReactNode | ComponentType<{conversation?: Conversation | null}>;
    input?: ReactNode | ComponentType<{conversation?: Conversation | null}>;
    footer?: ReactNode | ComponentType<{conversation?: Conversation | null}>;
    inlineDataComponents?: InlineDataComponentRegistry;
    messagePartComponents?: MessagePartComponentRegistry;
    errorBoundary?: ComponentType<{children: ReactNode}> | null;
    onClose?: AppCloseHandler;
    observability?: Observability;
    theme?: Theme;
    themeOverrides?: Partial<Theme>;
  }
>;

function ThreadsScreenWhenOpen({
  header,
  footer,
  content,
  log,
  input,
}: {
  header?: ReactNode | ComponentType<{conversation?: Conversation | null}>;
  footer?: ReactNode | ComponentType<{conversation?: Conversation | null}>;
  content?: ReactNode | ComponentType<{conversation?: Conversation | null}>;
  log?: ReactNode | ComponentType<{conversation?: Conversation | null}>;
  input?: ReactNode | ComponentType<{conversation?: Conversation | null}>;
}) {
  const {visibility} = useThreadsVisibility();

  if (visibility === Visibility.Closed) {
    return null;
  }

  return (
    <ThreadsConversationScreen
      header={header}
      content={content}
      log={log}
      input={input}
      footer={footer}
    />
  );
}

function ThreadsHistory({override}: {override?: ReactNode | ComponentType}) {
  const withHistory = useThreadsWithHistory();
  return withHistory ? (
    <ComponentWithOverride
      override={override}
      component={ThreadsHistoryDrawer}
    />
  ) : null;
}

export const ThreadsAssistant: FC<View.ViewProps<Props>> = createView<Props>(
  ({
    config,
    profile,
    withHistory = true,
    withLiveChat = false,
    clientFetch,
    parentMessageBus,
    historyDrawer,
    header,
    content,
    footer,
    log,
    input,
    inlineDataComponents,
    messagePartComponents,
    errorBoundary,
    onClose,
    observability,
    theme,
    themeOverrides,
    ...props
  }) => {
    const defaultErrorBoundary = useMemo(() => {
      return ({children}: {children: ReactNode}) => (
        <DefaultErrorBoundary config={config}>{children}</DefaultErrorBoundary>
      );
    }, [config]);

    const errorBoundaryOrDefault =
      errorBoundary === undefined ? defaultErrorBoundary : errorBoundary;

    const ErrorBoundaryComponent = errorBoundaryOrDefault;

    return (
      <view.div
        data-testid="threads-assistant-root"
        css={{height: 'fill', overflow: 'hidden'}}
        {...props}
      >
        <ThreadsAssistantProviders
          config={config}
          profile={profile}
          clientFetch={clientFetch}
          parentMessageBus={parentMessageBus}
          inlineDataComponents={inlineDataComponents}
          messagePartComponents={messagePartComponents}
          onClose={onClose}
          observability={observability}
          theme={theme}
          themeOverrides={themeOverrides}
          withHistory={withHistory}
        >
          <ThreadsContainer>
            {ErrorBoundaryComponent ? (
              <ErrorBoundaryComponent>
                <ThreadsScreenWhenOpen
                  header={header}
                  content={content}
                  log={log}
                  input={input}
                  footer={footer}
                />
              </ErrorBoundaryComponent>
            ) : (
              <ThreadsScreenWhenOpen
                header={header}
                content={content}
                log={log}
                input={input}
                footer={footer}
              />
            )}
            <ThreadsHistory override={historyDrawer} />
            {withLiveChat ? <ThreadsLiveChatIframe /> : null}
          </ThreadsContainer>
        </ThreadsAssistantProviders>
      </view.div>
    );
  },
);
